/* ==========================================================================
   custom-ab-facets.js
   Portable storefront filtering — Shopify native filters + Section Rendering API.

   - No jQuery, no dependencies.
   - Re-renders the section server-side, so pagination (page links, load-more,
     infinite scroll) keeps working without special handling.
   - Multiple independent instances per page are supported.

   Contract (see snippets/custom-ab-facets.liquid):
     [data-custom-ab-root]     wrapper, carries data-custom-ab-section="<id>"
     [data-custom-ab-form]     the GET form (toolbar + panel)
     [data-custom-ab-results]  grid + pagination, replaced on every update

   Events dispatched on document:
     custom-ab:updated  { detail: { root, url } }  after results are swapped
   ========================================================================== */

(function () {
  'use strict';

  // The snippet loads this file itself, so it can appear twice on a page if
  // rendered more than once. Same URL means one fetch but two executions.
  if (window.__customAbFacetsLoaded) return;
  window.__customAbFacetsLoaded = true;

  var SELECTORS = {
    root: '[data-custom-ab-root]',
    form: '[data-custom-ab-form]',
    results: '[data-custom-ab-results]',
    panel: '[data-custom-ab-panel]',
    overlay: '[data-custom-ab-overlay]',
    open: '[data-custom-ab-open]',
    close: '[data-custom-ab-close]',
    apply: '[data-custom-ab-apply]',
    clear: '[data-custom-ab-clear]',
    link: '[data-custom-ab-link]',
    range: '[data-custom-ab-range]'
  };

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var PRICE_DEBOUNCE = 600;

  // Must match --custom-ab-accordion-ms in the stylesheet.
  var ACCORDION_MS = 280;

  // Fallback grid selectors, used only when the install omitted the
  // .custom-ab-results wrapper. Override per-root with
  // data-custom-ab-results-selector.
  var DEFAULT_GRID = '.collection-matrix, .search-matrix, .product-list';

  /* ----------------------------------------------------------------------
     Shared scroll lock — one drawer open at a time. position:fixed rather
     than overflow:hidden, because iOS Safari ignores the latter on <body>.
     ---------------------------------------------------------------------- */
  var scrollLock = {
    y: 0,
    active: false,
    lock: function () {
      if (this.active) return;
      this.y = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.top = '-' + this.y + 'px';
      document.body.classList.add('custom-ab-scroll-locked');
      this.active = true;
    },
    unlock: function () {
      if (!this.active) return;
      document.body.classList.remove('custom-ab-scroll-locked');
      document.body.style.top = '';
      window.scrollTo(0, this.y);
      this.active = false;
    }
  };

  /* ---------------------------------------------------------------------- */

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Form state -> clean query string. Empty values and `page` are dropped so
  // changing a filter always lands the shopper on page 1.
  function formToQuery(form) {
    var params = new URLSearchParams(new FormData(form));
    var cleaned = new URLSearchParams();
    params.forEach(function (value, key) {
      if (value === null || String(value).trim() === '') return;
      if (key === 'page') return;
      cleaned.append(key, value);
    });
    return cleaned.toString();
  }

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  /* ----------------------------------------------------------------------
     Instance
     ---------------------------------------------------------------------- */

  function Facets(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-custom-ab-section');
    this.controller = null;
    this.token = 0;
    this.isOpen = false;
    this.lastFocused = null;

    this.onRootClick = this.onRootClick.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onResize = this.onResize.bind(this);
    this.applyDebounced = debounce(this.applyFromForm.bind(this), PRICE_DEBOUNCE);

    this.ensureResults();
    this.bind();
    this.markLinks();
    this.initRanges();
    this.measureStickyOffset();
    root.setAttribute('data-custom-ab-init', 'true');
  }

  // The desktop sidebar is sticky. Themes pin their own header, so measure it
  // and pin the card below it rather than hard-coding an offset per store.
  Facets.prototype.measureStickyOffset = function () {
    var tallest = 0;
    var candidates = document.querySelectorAll(
      'header, [class*="header" i], [id*="header" i], [data-sticky-header]'
    );

    Array.prototype.forEach.call(candidates, function (el) {
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return;

      // Read the declared offset rather than the current rect: at scroll 0 a
      // sticky header has not travelled to its pinned position yet, so its
      // rect.top says nothing about where it will settle.
      var stickTop = parseFloat(cs.top);
      if (isNaN(stickTop) || stickTop > 60) return;

      var height = el.getBoundingClientRect().height;
      if (!height) return;

      var occupies = stickTop + height;
      if (occupies > tallest) tallest = occupies;
    });

    // Ignore implausible measurements rather than pushing the card off-screen.
    if (tallest > 0 && tallest < window.innerHeight * 0.5) {
      this.root.style.setProperty('--custom-ab-sticky-top', Math.round(tallest) + 20 + 'px');
    }
  };

  Facets.prototype.form = function () {
    return this.root.querySelector(SELECTORS.form);
  };
  Facets.prototype.panel = function () {
    return this.root.querySelector(SELECTORS.panel);
  };
  Facets.prototype.results = function () {
    return this.root.querySelector(SELECTORS.results);
  };

  /* ---------- Results container ---------- */

  // The explicit .custom-ab-results wrapper is the supported install. When it
  // is missing, build one around the theme's grid so the module still works.
  Facets.prototype.ensureResults = function () {
    if (this.results()) return;

    var section = this.root.closest('[id^="shopify-section-"]') || document;
    var selector = this.root.getAttribute('data-custom-ab-results-selector') || DEFAULT_GRID;
    var grid = section.querySelector(selector);

    if (!grid || this.root.contains(grid)) return;

    var wrap = document.createElement('div');
    wrap.className = 'custom-ab-results';
    wrap.setAttribute('data-custom-ab-results', '');

    // Take the grid plus the pagination bits that follow it.
    var nodes = [grid];
    var next = grid.nextElementSibling;
    while (next) {
      if (
        next.matches('.load-more__icon, .js-load-more, .paginate') ||
        next.querySelector('.paginate, .js-load-more')
      ) {
        nodes.push(next);
        next = next.nextElementSibling;
      } else {
        break;
      }
    }

    nodes.forEach(function (node) {
      wrap.appendChild(node);
    });
    this.root.appendChild(wrap);
  };

  /* ---------- Binding (delegated, so swapped DOM stays live) ---------- */

  Facets.prototype.bind = function () {
    // Capture phase: InstantClick binds on bubble, so capturing lets us stop it
    // before it turns an AJAX click into a full page navigation.
    this.root.addEventListener('click', this.onRootClick, true);

    this.root.addEventListener(
      'change',
      function (event) {
        var target = event.target;
        if (!target) return;

        if (target.matches('input[type="checkbox"][name]')) {
          this.applyFromForm();
        } else if (target.matches('select[name="sort_by"]')) {
          this.applyFromForm();
        } else if (target.matches('[data-custom-ab-range-min], [data-custom-ab-range-max]')) {
          // Fires on handle release — apply immediately.
          this.applyFromForm();
        }
      }.bind(this)
    );

    this.root.addEventListener(
      'input',
      function (event) {
        var target = event.target;
        if (!target) return;

        if (target.matches('[data-custom-ab-range-min], [data-custom-ab-range-max]')) {
          this.onRangeInput(target);
        } else if (target.matches('[data-custom-ab-price]')) {
          this.onPriceInput(target);
          this.applyDebounced();
        }
      }.bind(this)
    );

    this.root.addEventListener(
      'submit',
      function (event) {
        if (event.target && event.target.matches(SELECTORS.form)) {
          event.preventDefault();
          this.close();
          this.applyFromForm();
        }
      }.bind(this)
    );

    document.addEventListener('keydown', this.onKeydown);
    // Backstop for the overlay: closes the drawer on any tap outside it, even
    // if a theme renders something above the overlay or restyles it.
    document.addEventListener('click', this.onOutsideClick);
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('resize', debounce(this.onResize, 150));
  };

  // InstantClick binds a capture-phase click listener on `document`, which runs
  // before ours and would turn an AJAX click into a full page load. It skips
  // anything carrying data-no-instant, so tag the links we intend to handle.
  Facets.prototype.markLinks = function () {
    var results = this.results();
    if (results) {
      results.querySelectorAll('a[href*="page="]').forEach(function (link) {
        if (link.closest('.js-load-more')) return;
        link.setAttribute('data-no-instant', '');
      });
    }
    this.root.querySelectorAll(SELECTORS.link).forEach(function (link) {
      link.setAttribute('data-no-instant', '');
    });
  };

  Facets.prototype.onRootClick = function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest(SELECTORS.open)) {
      event.preventDefault();
      this.open();
      return;
    }

    if (target.closest(SELECTORS.close) || target.closest(SELECTORS.overlay)) {
      event.preventDefault();
      this.close();
      return;
    }

    if (target.closest(SELECTORS.apply)) {
      // Results are already live; Apply just dismisses the drawer.
      event.preventDefault();
      this.close();
      return;
    }

    if (target.closest(SELECTORS.clear)) {
      event.preventDefault();
      this.clearAll();
      return;
    }

    // Accordion. Native <details> snaps open/shut with no transition, so take
    // the toggle over and drive the height ourselves.
    var summary = target.closest('.custom-ab-group__summary');
    if (summary) {
      event.preventDefault();
      this.toggleGroup(summary);
      return;
    }

    var chip = target.closest(SELECTORS.link);
    if (chip && chip.href) {
      event.preventDefault();
      event.stopPropagation();
      this.render(chip.href, true);
      return;
    }

    // Pagination. Matching on `page=` rather than a wrapper class keeps this
    // working with any theme's pagination markup.
    var results = this.results();
    if (!results || !results.contains(target)) return;

    var link = target.closest('a[href]');
    if (!link) return;
    // Turbo's load-more/infinite-scroll has its own delegated handler.
    if (link.closest('.js-load-more') || link.hasAttribute('data-custom-ab-ignore')) return;
    if (link.href.indexOf('page=') === -1) return;
    if (link.hostname !== window.location.hostname) return;

    event.preventDefault();
    event.stopPropagation();
    this.render(link.href, true, true);
  };

  // Slide a filter group open/shut.
  //
  // A <details> cannot be animated natively: removing `open` hides the content
  // instantly, so the closing frame never renders. The fix is to keep `open`
  // set until the height transition has finished, and only then drop it.
  // Heights are cleared afterwards so the group can reflow on its own (filter
  // lists change length as counts update).
  Facets.prototype.toggleGroup = function (summary) {
    var group = summary.closest('.custom-ab-group');
    if (!group) return;

    var content = group.querySelector('.custom-ab-group__content');
    if (!content) {
      group.open = !group.open;
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      group.open = !group.open;
      return;
    }

    // A second click mid-animation must not leave a stale timer behind.
    if (content.customAbTimer) {
      clearTimeout(content.customAbTimer);
      content.customAbTimer = null;
    }

    var duration = ACCORDION_MS;

    if (group.open) {
      content.style.height = content.scrollHeight + 'px';
      content.offsetHeight; // force a reflow so the next value transitions
      content.style.height = '0px';

      content.customAbTimer = setTimeout(function () {
        group.open = false;
        content.style.height = '';
        content.customAbTimer = null;
      }, duration);
    } else {
      group.open = true;
      content.style.height = '0px';
      content.offsetHeight;
      content.style.height = content.scrollHeight + 'px';

      content.customAbTimer = setTimeout(function () {
        content.style.height = '';
        content.customAbTimer = null;
      }, duration);
    }
  };

  Facets.prototype.onOutsideClick = function (event) {
    if (!this.isOpen) return;

    var panel = this.panel();
    var target = event.target;
    if (!panel || !target || !target.closest) return;

    if (panel.contains(target)) return; // inside the drawer
    if (target.closest(SELECTORS.open)) return; // the trigger that opened it

    this.close();
  };

  Facets.prototype.onKeydown = function (event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab' || event.keyCode === 9) {
      this.trapFocus(event);
    }
  };

  Facets.prototype.onPopState = function () {
    this.render(window.location.href, false);
  };

  Facets.prototype.onResize = function () {
    // The trigger is display:none once the sidebar layout kicks in; if the
    // viewport grew while the drawer was open, drop the modal state.
    if (!this.isOpen) return;
    var trigger = this.root.querySelector(SELECTORS.open);
    if (trigger && trigger.offsetParent === null) this.close();
  };

  /* ---------- Price range slider ---------- */

  Facets.prototype.initRanges = function () {
    var self = this;
    this.root.querySelectorAll(SELECTORS.range).forEach(function (wrap) {
      self.paintRange(wrap);
    });
  };

  Facets.prototype.paintRange = function (wrap) {
    var minEl = wrap.querySelector('[data-custom-ab-range-min]');
    var maxEl = wrap.querySelector('[data-custom-ab-range-max]');
    var fill = wrap.querySelector('[data-custom-ab-range-fill]');
    if (!minEl || !maxEl || !fill) return;

    var hi = num(wrap.getAttribute('data-range-max'), 0);
    if (hi <= 0) return;

    var a = num(minEl.value, 0);
    var b = num(maxEl.value, hi);

    // A native range thumb travels between thumbWidth/2 and width-thumbWidth/2,
    // not 0..width. Match that geometry so the fill lines up with the handle
    // centres instead of drifting at the ends.
    var width = wrap.getBoundingClientRect().width;
    var thumb = 18;

    if (!width) {
      fill.style.left = (a / hi) * 100 + '%';
      fill.style.right = 100 - (b / hi) * 100 + '%';
      return;
    }

    var usable = width - thumb;
    var leftPx = thumb / 2 + (a / hi) * usable;
    var rightPx = thumb / 2 + (b / hi) * usable;

    fill.style.left = leftPx + 'px';
    fill.style.right = width - rightPx + 'px';
  };

  // Dragging a handle: keep the two from crossing, then mirror into the
  // number inputs so the form carries the real filter params.
  Facets.prototype.onRangeInput = function (input) {
    var wrap = input.closest(SELECTORS.range);
    if (!wrap) return;

    var minEl = wrap.querySelector('[data-custom-ab-range-min]');
    var maxEl = wrap.querySelector('[data-custom-ab-range-max]');
    var hi = num(wrap.getAttribute('data-range-max'), 0);
    var a = num(minEl.value, 0);
    var b = num(maxEl.value, hi);

    if (a > b) {
      if (input === minEl) {
        a = b;
        minEl.value = a;
      } else {
        b = a;
        maxEl.value = b;
      }
    }

    var group = wrap.parentNode;
    var priceMin = group.querySelector('[data-custom-ab-price-min]');
    var priceMax = group.querySelector('[data-custom-ab-price-max]');

    // Blank a field once its handle is back at the extreme, so no price param
    // is submitted and the filter reads as cleared. The epsilon absorbs float
    // error — an exact compare leaves a phantom filter (and a lit badge) when
    // the value lands a hair short of the bound.
    if (priceMin) priceMin.value = a <= 0 ? '' : a;
    if (priceMax) priceMax.value = b >= hi - 0.01 ? '' : b;

    this.paintRange(wrap);
  };

  // Typing in a number input: mirror back onto the handles.
  Facets.prototype.onPriceInput = function (input) {
    var group = input.closest('.custom-ab-group__content') || this.root;
    var wrap = group.querySelector(SELECTORS.range);
    if (!wrap) return;

    var hi = num(wrap.getAttribute('data-range-max'), 0);
    var minEl = wrap.querySelector('[data-custom-ab-range-min]');
    var maxEl = wrap.querySelector('[data-custom-ab-range-max]');

    if (input.hasAttribute('data-custom-ab-price-min') && minEl) {
      minEl.value = num(input.value, 0);
    } else if (input.hasAttribute('data-custom-ab-price-max') && maxEl) {
      maxEl.value = num(input.value, hi);
    }

    this.paintRange(wrap);
  };

  /* ---------- Drawer ---------- */

  Facets.prototype.open = function () {
    var panel = this.panel();
    var overlay = this.root.querySelector(SELECTORS.overlay);
    if (!panel) return;

    this.lastFocused = document.activeElement;
    this.isOpen = true;

    panel.classList.add('is-open');
    if (overlay) {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
    }

    var trigger = this.root.querySelector(SELECTORS.open);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    scrollLock.lock();

    // The panel is visibility:hidden until the is-open class is style-resolved,
    // and .focus() is a no-op on a non-rendered element — so wait a frame.
    var closeBtn = panel.querySelector(SELECTORS.close);
    if (closeBtn) {
      requestAnimationFrame(function () {
        closeBtn.focus();
      });
    }
  };

  Facets.prototype.close = function () {
    if (!this.isOpen) return;

    var panel = this.panel();
    var overlay = this.root.querySelector(SELECTORS.overlay);

    this.isOpen = false;
    if (panel) panel.classList.remove('is-open');
    if (overlay) {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
    }

    var trigger = this.root.querySelector(SELECTORS.open);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    scrollLock.unlock();

    if (this.lastFocused && this.lastFocused.focus) {
      this.lastFocused.focus();
    } else if (trigger) {
      trigger.focus();
    }
  };

  Facets.prototype.trapFocus = function (event) {
    var panel = this.panel();
    if (!panel) return;

    var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- Applying ---------- */

  Facets.prototype.applyFromForm = function () {
    var form = this.form();
    if (!form) return;

    var action = form.getAttribute('action') || window.location.pathname;
    var pathname = action.split('?')[0];
    var query = formToQuery(form);

    this.render(query ? pathname + '?' + query : pathname, true);
  };

  Facets.prototype.clearAll = function () {
    var form = this.form();
    if (!form) return;

    form.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
      input.checked = false;
    });
    form.querySelectorAll('[data-custom-ab-price]').forEach(function (input) {
      input.value = '';
    });
    form.querySelectorAll(SELECTORS.range).forEach(function (wrap) {
      var hi = num(wrap.getAttribute('data-range-max'), 0);
      var minEl = wrap.querySelector('[data-custom-ab-range-min]');
      var maxEl = wrap.querySelector('[data-custom-ab-range-max]');
      if (minEl) minEl.value = 0;
      if (maxEl) maxEl.value = hi;
    });

    this.initRanges();
    this.applyFromForm();
  };

  /* ---------- Fetch + swap ---------- */

  Facets.prototype.render = function (url, pushState, scrollToTop) {
    var self = this;

    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    // Token guard: an aborted request settles *after* its replacement has
    // already set is-loading, so only the newest request may clear it.
    var token = this.token + 1;
    this.token = token;

    var target = new URL(url, window.location.origin);
    var cleanUrl = target.pathname + target.search;

    var fetchUrl = new URL(target.href);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    this.root.classList.add('is-loading');

    var options = { headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (this.controller) options.signal = this.controller.signal;

    fetch(fetchUrl.toString(), options)
      .then(function (response) {
        if (!response.ok) throw new Error('Section render failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (token !== self.token) return;
        self.swap(html, cleanUrl, pushState, scrollToTop);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (token !== self.token) return;
        // Never leave the shopper stuck — fall back to a real navigation.
        window.location.href = cleanUrl;
      })
      .finally(function () {
        if (token !== self.token) return;
        self.root.classList.remove('is-loading');
        self.controller = null;
      });
  };

  Facets.prototype.swap = function (html, cleanUrl, pushState, scrollToTop) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');

    var newResults = parsed.querySelector(SELECTORS.results);
    var currentResults = this.results();

    if (!newResults || !currentResults) {
      window.location.href = cleanUrl;
      return;
    }

    currentResults.innerHTML = newResults.innerHTML;

    // Filter panel — counts and available values change with every selection.
    // Preserve each group's open/closed state so the panel doesn't visually
    // reset under the shopper's finger.
    var newForm = parsed.querySelector(SELECTORS.form);
    var currentForm = this.form();

    if (newForm && currentForm) {
      var openState = {};
      currentForm.querySelectorAll('details.custom-ab-group').forEach(function (group, i) {
        openState[i] = group.open;
      });

      currentForm.innerHTML = newForm.innerHTML;

      currentForm.querySelectorAll('details.custom-ab-group').forEach(function (group, i) {
        if (typeof openState[i] === 'boolean') group.open = openState[i];
      });

      // innerHTML replacement drops the panel's open class.
      if (this.isOpen) {
        var panel = this.panel();
        var overlay = this.root.querySelector(SELECTORS.overlay);
        if (panel) panel.classList.add('is-open');
        if (overlay) {
          overlay.classList.add('is-open');
          overlay.setAttribute('aria-hidden', 'false');
        }
        var trigger = this.root.querySelector(SELECTORS.open);
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
    }

    this.markLinks();
    this.initRanges();

    if (pushState && window.history && window.history.pushState) {
      window.history.pushState({ customAbFacets: true }, '', cleanUrl);
    }

    if (scrollToTop) this.scrollToResults();

    document.dispatchEvent(
      new CustomEvent('custom-ab:updated', {
        bubbles: true,
        detail: { root: this.root, url: cleanUrl }
      })
    );

    this.reinitPagination();

    // Nudge lazy-loaders and grid scripts that only listen for these.
    window.dispatchEvent(new Event('resize'));
    window.dispatchEvent(new Event('scroll'));
  };

  // Turbo's infinite scroll captures the .collection-matrix element when it
  // builds its Waypoint, so swapping the grid leaves that Waypoint bound to a
  // detached node. Re-init it against the new element.
  //
  // Deliberately NOT re-calling enableLoadMoreProducts/enableLoadMoreSearch:
  // those attach delegated handlers to <body>, which already survive the swap.
  // Calling them again would stack handlers and load each page twice.
  Facets.prototype.reinitPagination = function () {
    if (this.root.getAttribute('data-custom-ab-pagination') !== 'infinite_scroll') return;

    var results = this.results();
    if (!results) return;

    if (results.querySelector('.collection-matrix') && typeof window.enableInfiniteScroll === 'function') {
      window.enableInfiniteScroll();
    } else if (results.querySelector('.search-matrix') && typeof window.enableInfiniteSearchScroll === 'function') {
      window.enableInfiniteSearchScroll();
    }
  };

  Facets.prototype.scrollToResults = function () {
    var results = this.results();
    if (!results) return;

    var top = results.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({
      top: top < 0 ? 0 : top,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };

  /* ---------------------------------------------------------------------- */

  function init() {
    document.querySelectorAll(SELECTORS.root).forEach(function (root) {
      if (root.getAttribute('data-custom-ab-init') === 'true') return;
      new Facets(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('page:loaded', init);
})();